import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, X, Plus, Trash2, Eye, EyeOff } from "lucide-react";

const AMENITY_PRESETS = [
  { key: "wifi", label: "Wi-Fi" },
  { key: "ac", label: "Air Conditioning" },
  { key: "kitchen", label: "Kitchen" },
  { key: "tv", label: "TV" },
  { key: "parking", label: "Parking" },
  { key: "sea_view", label: "Sea View" },
  { key: "balcony", label: "Balcony" },
  { key: "garden", label: "Garden" },
  { key: "family", label: "Family Friendly" },
  { key: "pool", label: "Pool" },
  { key: "bbq", label: "BBQ" },
  { key: "washing_machine", label: "Washing Machine" },
  { key: "pets", label: "Pets Allowed" },
];

function PhotoPicker({ value = [], onChange }) {
  const [pool, setPool] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/admin/photos")
      .then((r) => setPool(r.data))
      .catch((err) => console.error("Failed to load photos:", err));
  }, [open]);

  const add = (url) => {
    if (value.includes(url)) return;
    onChange([...value, url]);
  };
  const remove = (url) => onChange(value.filter((u) => u !== url));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const a = [...value];
    [a[i], a[j]] = [a[j], a[i]];
    onChange(a);
  };

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
        {value.map((url, i) => (
          <div key={url + i} className="relative group rounded-lg overflow-hidden border border-[#E9E2D3]">
            <img src={url} alt="" className="w-full h-20 object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1">
              <button onClick={() => move(i, -1)} className="text-white text-xs bg-white/20 px-1.5 rounded" title="left">◀</button>
              <button onClick={() => remove(url)} className="text-white bg-red-500 w-6 h-6 rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
              <button onClick={() => move(i, 1)} className="text-white text-xs bg-white/20 px-1.5 rounded" title="right">▶</button>
            </div>
            <span className="absolute top-1 left-1 bg-white/90 text-[10px] px-1.5 py-0.5 rounded">{i + 1}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="text-xs text-[#1A2B4C] border border-[#E9E2D3] px-3 py-1.5 rounded-full hover:bg-[#1A2B4C] hover:text-white transition-all"
          data-testid="photo-picker-toggle"
        >
          {open ? "Hide" : "+ Add from library"}
        </button>
        <input
          placeholder="Or paste image URL"
          className="flex-1 min-w-[200px] text-xs bg-[#F5F2EB] border border-[#E9E2D3] rounded-full px-3 py-1.5"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value) {
              add(e.target.value);
              e.target.value = "";
            }
          }}
        />
      </div>
      {open && (
        <div className="mt-3 p-3 bg-[#F5F2EB] rounded-xl max-h-60 overflow-y-auto">
          {pool.length === 0 && <p className="text-xs text-[#6B705C]">No uploaded photos. Go to Photos tab.</p>}
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {pool.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => add(p.url)}
                className="rounded overflow-hidden border border-transparent hover:border-[#1A2B4C]"
              >
                <img src={p.url} alt="" className="w-full h-16 object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AmenitiesEditor({ value = [], onChange }) {
  const [custom, setCustom] = useState("");
  const presetKeys = AMENITY_PRESETS.map((a) => a.key);
  const customs = value.filter((v) => !presetKeys.includes(v));

  const toggle = (key) => {
    if (value.includes(key)) onChange(value.filter((v) => v !== key));
    else onChange([...value, key]);
  };
  const addCustom = () => {
    const v = custom.trim().toLowerCase().replace(/\s+/g, "_");
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
    setCustom("");
  };
  const removeCustom = (v) => onChange(value.filter((x) => x !== v));

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {AMENITY_PRESETS.map((a) => {
          const on = value.includes(a.key);
          return (
            <label
              key={a.key}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs transition-all ${
                on
                  ? "bg-[#1A2B4C] text-white border-[#1A2B4C]"
                  : "bg-[#F5F2EB] text-[#1A2B4C] border-[#E9E2D3] hover:border-[#1A2B4C]"
              }`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(a.key)}
                className="accent-[#1A2B4C]"
                data-testid={`amenity-${a.key}`}
              />
              {a.label}
            </label>
          );
        })}
      </div>
      {customs.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {customs.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-[#1A2B4C]/10 text-[#1A2B4C] text-xs px-3 py-1 rounded-full"
            >
              {v}
              <button
                type="button"
                onClick={() => removeCustom(v)}
                className="hover:text-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-3">
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
          placeholder="Add custom amenity (e.g. jacuzzi)"
          className="flex-1 text-xs bg-[#F5F2EB] border border-[#E9E2D3] rounded-full px-3 py-1.5"
          data-testid="amenity-custom-input"
        />
        <button
          type="button"
          onClick={addCustom}
          className="text-xs text-white bg-[#1A2B4C] px-4 py-1.5 rounded-full hover:opacity-90"
          data-testid="amenity-custom-add"
        >
          Add
        </button>
      </div>
    </div>
  );
}

function ApartmentForm({ apt, isNew = false, onSaved, onCancel, onDeleted, onToggled }) {
  const [form, setForm] = useState(apt);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const isPublished = form.published !== false;

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const buildPayload = () => ({
    name_el: form.name_el || "",
    name_en: form.name_en || "",
    description_el: form.description_el || "",
    description_en: form.description_en || "",
    capacity: Number(form.capacity) || 1,
    bedrooms: Number(form.bedrooms) || 1,
    bathrooms: Number(form.bathrooms) || 1,
    price_per_night: Number(form.price_per_night) || 0,
    images: form.images || [],
    amenities: form.amenities || [],
    airbnb_url: form.airbnb_url || "",
    booking_url: form.booking_url || "",
    map_url: form.map_url || "",
    latitude: form.latitude === "" || form.latitude == null ? null : Number(form.latitude),
    longitude: form.longitude === "" || form.longitude == null ? null : Number(form.longitude),
    min_nights:
      form.min_nights === "" || form.min_nights == null
        ? null
        : Math.max(1, parseInt(form.min_nights, 10) || 1),
    seasonal_pricing: (form.seasonal_pricing || []).map((r) => ({
      label: r.label || "",
      start: r.start,
      end: r.end,
      price: Number(r.price),
    })),
  });

  const save = async () => {
    if (isNew && (!form.name_el?.trim() || !form.name_en?.trim())) {
      toast.error("Name (EL & EN) is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...buildPayload(), id: (form.id || "").trim() || undefined };
        const r = await api.post("/admin/apartments", payload);
        toast.success(`Apartment created: ${r.data.id}`);
      } else {
        await api.put(`/admin/apartments/${form.id}`, buildPayload());
        toast.success("Saved");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (force = false) => {
    setDeleting(true);
    try {
      await api.delete(`/admin/apartments/${form.id}${force ? "?force=true" : ""}`);
      toast.success("Apartment deleted");
      onDeleted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 409 && detail.includes("active booking")) {
        const ok = window.confirm(
          `${detail}\n\nDo you REALLY want to delete this apartment along with its active bookings? This cannot be undone.`
        );
        if (ok) {
          setDeleting(false);
          return doDelete(true);
        }
      } else {
        toast.error(detail || "Delete failed");
      }
    } finally {
      setDeleting(false);
    }
  };

  const askDelete = () => {
    if (!window.confirm(`Delete apartment "${form.name_el || form.id}"? This cannot be undone.`)) return;
    doDelete(false);
  };

  const togglePublish = async () => {
    const nextPublished = !isPublished;
    if (!window.confirm(
      nextPublished
        ? `Publish "${form.name_el || form.id}" and make it visible on the public site?`
        : `Unpublish "${form.name_el || form.id}"? It will be hidden from the public site immediately. Bookings already made are not affected.`
    )) return;
    setToggling(true);
    try {
      const r = await api.post(`/admin/apartments/${form.id}/publish`, { published: nextPublished });
      setForm({ ...form, published: r.data.published });
      toast.success(r.data.published ? "Apartment published" : "Apartment unpublished");
      onToggled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change status");
    } finally {
      setToggling(false);
    }
  };

  const label = "text-xs uppercase tracking-widest text-[#6B705C] mb-1.5 block";
  const input = "w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-3 py-2.5 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30 text-sm";

  return (
    <div
      className={`bg-white rounded-2xl border p-6 mb-6 relative ${
        !isNew && !isPublished ? "border-amber-300 bg-amber-50/40" : "border-[#E9E2D3]"
      }`}
      data-testid={isNew ? "new-apartment-form" : `apartment-form-${form.id}`}
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-serif text-xl text-[#1A2B4C]">
            {isNew ? "New Apartment" : (form.name_el || form.id)}
          </h3>
          {!isNew && !isPublished && (
            <span
              data-testid={`apt-unpublished-badge-${form.id}`}
              className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-amber-300"
            >
              <EyeOff className="w-3 h-3" /> Unpublished
            </span>
          )}
        </div>
        {isNew && (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-[#6B705C] hover:text-red-500 inline-flex items-center gap-1"
            data-testid="cancel-new-apartment"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
        )}
      </div>

      {isNew && (
        <div className="mb-4">
          <label className={label}>Slug / ID (optional — auto-generated if empty)</label>
          <input
            className={input}
            placeholder="e.g. muses-suite"
            value={form.id || ""}
            onChange={(e) => upd("id", e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))}
            data-testid="new-apt-id"
          />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className={label}>Name (EL) *</label>
          <input className={input} value={form.name_el || ""} onChange={(e) => upd("name_el", e.target.value)} data-testid="apt-name-el" />
        </div>
        <div>
          <label className={label}>Name (EN) *</label>
          <input className={input} value={form.name_en || ""} onChange={(e) => upd("name_en", e.target.value)} data-testid="apt-name-en" />
        </div>
      </div>
      <div className="mb-4">
        <label className={label}>Description (EL)</label>
        <textarea rows="4" className={input} value={form.description_el || ""} onChange={(e) => upd("description_el", e.target.value)} data-testid="apt-desc-el" />
      </div>
      <div className="mb-4">
        <label className={label}>Description (EN)</label>
        <textarea rows="4" className={input} value={form.description_en || ""} onChange={(e) => upd("description_en", e.target.value)} data-testid="apt-desc-en" />
      </div>
      <div className="grid sm:grid-cols-4 gap-3 mb-4">
        <div>
          <label className={label}>Capacity</label>
          <input type="number" className={input} value={form.capacity || 0} onChange={(e) => upd("capacity", e.target.value)} data-testid="apt-capacity" />
        </div>
        <div>
          <label className={label}>Bedrooms</label>
          <input type="number" className={input} value={form.bedrooms || 0} onChange={(e) => upd("bedrooms", e.target.value)} data-testid="apt-bedrooms" />
        </div>
        <div>
          <label className={label}>Bathrooms</label>
          <input type="number" className={input} value={form.bathrooms || 0} onChange={(e) => upd("bathrooms", e.target.value)} data-testid="apt-bathrooms" />
        </div>
        <div>
          <label className={label}>€ / night</label>
          <input type="number" step="0.01" className={input} value={form.price_per_night || 0} onChange={(e) => upd("price_per_night", e.target.value)} data-testid="apt-price" />
        </div>
      </div>
      <div className="mb-4">
        <label className={label}>Minimum stay (nights)</label>
        <input
          type="number"
          min="1"
          step="1"
          className={input + " sm:max-w-[200px]"}
          placeholder="Leave empty for global default"
          value={form.min_nights ?? ""}
          onChange={(e) => upd("min_nights", e.target.value)}
          data-testid="apt-min-nights"
        />
        <p className="text-[10px] text-[#6B705C] mt-1">
          Overrides the site-wide default. Empty = use the global setting from Settings tab.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className={label}>Booking.com URL</label>
          <input className={input} value={form.booking_url || ""} onChange={(e) => upd("booking_url", e.target.value)} />
        </div>
        <div>
          <label className={label}>Airbnb URL</label>
          <input className={input} value={form.airbnb_url || ""} onChange={(e) => upd("airbnb_url", e.target.value)} />
        </div>
      </div>
      <div className="mb-4 p-4 bg-[#F5F2EB]/60 rounded-xl border border-[#E9E2D3]">
        <label className={label}>Location — Google Maps URL or Coordinates</label>
        <p className="text-[10px] text-[#6B705C] mb-2">
          Paste a Google Maps share link (short or full) OR fill in latitude/longitude. Guests will see a &quot;View on map&quot; button.
        </p>
        <input
          className={input + " mb-3"}
          placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/..."
          value={form.map_url || ""}
          onChange={(e) => upd("map_url", e.target.value)}
          data-testid="apt-map-url"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Latitude</label>
            <input
              type="number"
              step="any"
              className={input}
              placeholder="e.g. 40.2115"
              value={form.latitude ?? ""}
              onChange={(e) => upd("latitude", e.target.value)}
              data-testid="apt-latitude"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Longitude</label>
            <input
              type="number"
              step="any"
              className={input}
              placeholder="e.g. 23.6706"
              value={form.longitude ?? ""}
              onChange={(e) => upd("longitude", e.target.value)}
              data-testid="apt-longitude"
            />
          </div>
        </div>
      </div>
      <div className="mb-4">
        <label className={label}>Amenities</label>
        <AmenitiesEditor value={form.amenities || []} onChange={(v) => upd("amenities", v)} />
      </div>
      <div className="mb-5 pt-5 border-t border-[#E9E2D3]">
        <div className="flex items-center justify-between mb-2">
          <label className={label + " mb-0"}>Seasonal pricing (overrides base price for these date ranges)</label>
          <button
            type="button"
            onClick={() =>
              upd("seasonal_pricing", [
                ...(form.seasonal_pricing || []),
                { label: "", start: "", end: "", price: form.price_per_night || 0 },
              ])
            }
            className="text-xs text-[#1A2B4C] border border-[#E9E2D3] px-3 py-1 rounded-full hover:bg-[#1A2B4C] hover:text-white transition-all"
          >
            + Add season
          </button>
        </div>
        <p className="text-[10px] text-[#6B705C] mb-3">
          Base price (€{form.price_per_night || 0}/night) applies when no rule matches.
        </p>
        {(form.seasonal_pricing || []).length === 0 ? (
          <p className="text-xs text-[#6B705C]">No seasonal rules. Add one to charge different rates per period.</p>
        ) : (
          <div className="space-y-2">
            {(form.seasonal_pricing || []).map((rule, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Label</label>
                  <input
                    className={input}
                    placeholder="e.g. High season"
                    value={rule.label || ""}
                    onChange={(e) => {
                      const arr = [...(form.seasonal_pricing || [])];
                      arr[i] = { ...arr[i], label: e.target.value };
                      upd("seasonal_pricing", arr);
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">From</label>
                  <input
                    type="date"
                    className={input}
                    value={rule.start || ""}
                    onChange={(e) => {
                      const arr = [...(form.seasonal_pricing || [])];
                      arr[i] = { ...arr[i], start: e.target.value };
                      upd("seasonal_pricing", arr);
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">To</label>
                  <input
                    type="date"
                    className={input}
                    value={rule.end || ""}
                    onChange={(e) => {
                      const arr = [...(form.seasonal_pricing || [])];
                      arr[i] = { ...arr[i], end: e.target.value };
                      upd("seasonal_pricing", arr);
                    }}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">€/night</label>
                  <input
                    type="number"
                    step="0.01"
                    className={input}
                    value={rule.price ?? ""}
                    onChange={(e) => {
                      const arr = [...(form.seasonal_pricing || [])];
                      arr[i] = { ...arr[i], price: e.target.value };
                      upd("seasonal_pricing", arr);
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const arr = (form.seasonal_pricing || []).filter((_, idx) => idx !== i);
                    upd("seasonal_pricing", arr);
                  }}
                  className="col-span-1 text-red-500 hover:bg-red-50 rounded-lg py-2"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mb-5">
        <label className={label}>Images (drag thumbnails to reorder, first one is shown as main)</label>
        <PhotoPicker value={form.images || []} onChange={(v) => upd("images", v)} />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={save}
          disabled={saving}
          data-testid={isNew ? "create-apt-submit" : `save-apt-${form.id}`}
          className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : isNew ? "Create apartment" : "Save changes"}
        </button>
        {!isNew && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={togglePublish}
              disabled={toggling}
              data-testid={`toggle-publish-${form.id}`}
              className={`inline-flex items-center gap-2 border px-4 py-2.5 rounded-full text-xs uppercase tracking-widest disabled:opacity-50 transition-colors ${
                isPublished
                  ? "text-amber-700 border-amber-300 bg-white hover:bg-amber-50"
                  : "text-emerald-700 border-emerald-300 bg-white hover:bg-emerald-50"
              }`}
              title={isPublished ? "Hide this apartment from the public site" : "Show this apartment on the public site"}
            >
              {isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {toggling ? "..." : isPublished ? "Unpublish" : "Publish"}
            </button>
            <button
              onClick={askDelete}
              disabled={deleting}
              data-testid={`delete-apt-${form.id}`}
              className="inline-flex items-center gap-2 text-red-600 border border-red-300 bg-white px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Deleting..." : "Delete apartment"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const EMPTY_APT = {
  id: "",
  name_el: "",
  name_en: "",
  description_el: "",
  description_en: "",
  capacity: 2,
  bedrooms: 1,
  bathrooms: 1,
  price_per_night: 100,
  images: [],
  amenities: ["wifi", "ac"],
  airbnb_url: "",
  booking_url: "",
  seasonal_pricing: [],
};

export default function ApartmentsTab() {
  const [apts, setApts] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/apartments");
      setApts(r.data);
    } catch (e) {
      console.error("Failed to load apartments:", e);
      toast.error("Failed to load apartments");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#1A2B4C] mb-1">Apartments Editor</h2>
          <p className="text-sm text-[#6B705C]">
            Edit or add apartments — titles, descriptions (EL + EN), pricing, capacity, amenities and photos.
          </p>
        </div>
        {!showNew && (
          <button
            onClick={() => setShowNew(true)}
            data-testid="add-new-apartment-btn"
            className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-5 py-2.5 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform"
          >
            <Plus className="w-4 h-4" />
            Add new apartment
          </button>
        )}
      </div>

      {showNew && (
        <ApartmentForm
          apt={EMPTY_APT}
          isNew
          onSaved={() => {
            setShowNew(false);
            load();
          }}
          onCancel={() => setShowNew(false)}
        />
      )}

      {apts.map((a) => (
        <ApartmentForm
          key={a.id}
          apt={a}
          onSaved={load}
          onDeleted={load}
          onToggled={load}
        />
      ))}
    </div>
  );
}
